/**
 * useAccountInfoItems
 *
 * Returns the list of items rendered inside the AccountInfoSheet.
 *
 * Flutter source: lib/presentation/bottom_sheet/account_info_bottom_sheet.dart
 * (the `_accountItems` list at lines 33-82, plus the admin `insert(0, ...)` at
 * lines 88-100). The web port omits the Settings entry (Settings lives on
 * `UserActionsSheet` on web). The Admin entry is prepended as the first item,
 * gated on the current user being an admin / super-admin, mirroring Flutter's
 * admin gate (`account_info_bottom_sheet.dart:88-100`) and the predicate the
 * legacy `HomeTopBarBody` admin row used.
 *
 * Order and icon paths mirror the entries removed from `HomeTopBarBody.tsx`
 * so the visual flow remains identical.
 */

import { useSelector } from 'react-redux';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import type { RootState } from '@/presentation/store';
import type { AccountInfoEntity } from '@/domain/entities/accountInfo/accountInfoEntity';
import { AccountInfoType } from '@/domain/enums/accountInfo/accountInfoType';
import { UserType } from '@/domain/enums/userType';

export const useAccountInfoItems = (): AccountInfoEntity[] => {
  const { l10n } = useLocale();
  const isAdmin = useSelector(
    (state: RootState) =>
      state.auth.user?.type === UserType.admin ||
      state.auth.user?.type === UserType.superAdmin,
  );

  return [
    ...(isAdmin
      ? [
        {
          type: AccountInfoType.ADMIN,
          title: l10n.admin.entryTitle,
          description: l10n.admin.entryDescription,
          iconPath: '/assets/images/avatar.png',
        },
      ]
      : []),
    {
      type: AccountInfoType.WALLET,
      title: l10n.wallet.title,
      description: l10n.accountInfo.walletDescription,
      iconPath: '/assets/images/wallet.png',
    },
    {
      type: AccountInfoType.UNLOCKED_VIDEOS,
      title: l10n.accountInfo.unlockedVideos,
      description: l10n.accountInfo.unlockedVideosDescription,
      iconPath: '/assets/images/unlocked.png',
    },
    {
      type: AccountInfoType.SUBSCRIPTIONS,
      title: l10n.accountInfo.subscriptions,
      description: l10n.accountInfo.subscriptionsDescription,
      iconPath: '/assets/images/subscriptions.png',
    },
    {
      type: AccountInfoType.SUBSCRIBERS,
      title: l10n.accountInfo.subscribers,
      description: l10n.accountInfo.subscribersDescription,
      iconPath: '/assets/images/subscribers.png',
    },
    {
      type: AccountInfoType.LIKES,
      title: l10n.accountInfo.likes,
      description: l10n.accountInfo.likesDescription,
      iconPath: '/assets/images/heart.png',
    },
    {
      type: AccountInfoType.WATCH_LATER,
      title: l10n.accountInfo.watchLater,
      description: l10n.accountInfo.watchLaterDescription,
      iconPath: '/assets/images/saved.png',
    },
    {
      type: AccountInfoType.FAVOURITE_PEOPLE,
      title: l10n.accountInfo.favouritePeople,
      description: l10n.accountInfo.favouritePeopleDescription,
      iconPath: '/assets/images/star.png',
    },
  ];
};
